import React, { useEffect, useState } from "react";
import { Loader, Select } from "@mantine/core";
import type {
  FieldComponentProps,
  ReviewItemData,
} from "@/pages/ClientProfile/types";
import { OPTIONS_MAP } from "@smartwiz/shared-types";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import { showFieldError } from "@utils/reviewFieldUtils.ts";

interface SelectFieldProps extends FieldComponentProps {
  options?: any[];
  item: ReviewItemData & { value?: any };
  mutationType?: "profile" | "ocr";
  formId?: string;
}

const SelectField: React.FC<SelectFieldProps> = ({
  commonProps,
  field,
  options,
  item,
  mutationType: _mutationType = "profile",
  formId: _formId,
}) => {
  const { userId } = useParams();
  const updateProfileField = useProfileStore(
    (state) => state.updateProfileField
  );
  const [loadingState, setLoadingState] = useState(false);

  // ensure optimisticValue state reflects the actual field value
  useEffect(() => {
    setOptimisticValue(field.value ?? "");
  }, [field.value]);

  // Normalize options
  const normalizeOptions = (opts: any[] | undefined) => {
    if (!opts) return [];
    return opts.map((option) => {
      if (typeof option === "boolean") {
        return option ? "true" : "false";
      }
      if (typeof option === "object" && option !== null) {
        // Handle objects by using their value, label, or name property, or JSON.stringify as fallback
        return (
          option.value ?? option.label ?? option.name ?? JSON.stringify(option)
        );
      }
      return String(option);
    });
  };

  const optionKey = options?.[0];

  const rawSelectOptions =
    optionKey && typeof optionKey === "string" && OPTIONS_MAP[optionKey]
      ? [...OPTIONS_MAP[optionKey]]
      : (options ?? []);

  const selectOptions = [...new Set(normalizeOptions(rawSelectOptions))];

  const normalizeValue = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "object" && value !== null) {
      // Handle objects by using their value, label, or name property, or JSON.stringify as fallback
      return value.value ?? value.label ?? value.name ?? JSON.stringify(value);
    }
    return String(value);
  };

  const [optimisticValue, setOptimisticValue] = useState<string>(
    normalizeValue(item.value ?? field.value)
  );

  /**
   * Handles changes to the Select field value with proper type conversion.
   *
   * This function performs a multi-step transformation:
   * 1. Normalizes the raw input to a consistent string format for UI display
   * 2. Converts back to the original data type for backend API communication
   * 3. Updates the UI optimistically with the normalized string
   * 4. Updates the form field with the original data type
   * 5. Sends the original data type to the backend API
   *
   * The transformation is necessary because:
   * - The Mantine Select component only works with strings for display
   * - The backend API expects the original data types (boolean, number, object, etc.)
   * - We need to maintain data integrity while providing a consistent UI experience
   *
   * @param value - The raw string value from the Mantine Select component (can be null when cleared)
   */
  const handleChange = async (value: string | null): Promise<void> => {
    // Step 1: Normalize the raw input to a consistent string format for UI
    const normalizedValue = normalizeValue(value);
    setLoadingState(true);

    // Step 2: Convert back to original data type for backend API
    // The Select component only works with strings, but we need to preserve original data types
    let originalDataTypeValue: any = normalizedValue;

    if (rawSelectOptions.length > 0) {
      // Find the original option that matches our normalized value
      const originalOption = rawSelectOptions.find(
        (opt) => normalizeValue(opt) === normalizedValue
      );

      // If found, use the original data type (boolean, number, object, etc.)
      if (originalOption !== undefined) {
        originalDataTypeValue = originalOption;
      }
    }

    // Step 3: Update UI optimistically with normalized string
    setOptimisticValue(originalDataTypeValue);

    // Step 4: Update form field with original data type
    field.onChange(originalDataTypeValue);

    // Step 5: Send original data type to backend API
    try {
      await updateProfileField(userId ?? "", field.name, originalDataTypeValue);
      // Success notification is shown by the store method
      setLoadingState(false);
    } catch (error: any) {
      showFieldError(error, field.name);
      // Revert optimistic update on error
      setOptimisticValue(normalizeValue(item.value ?? field.value));
      setLoadingState(false);
    }
  };

  const handleBlur = (): void => {
    field.onBlur();
  };

  const isValidValue = selectOptions.includes(optimisticValue);
  const displayValue = isValidValue ? optimisticValue : "";

  return (
    <Select
      {...commonProps}
      {...field}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      data={selectOptions.map((option) => ({
        value: option,
        label: option,
      }))}
      variant="filled"
      searchable
      nothingFoundMessage="Nothing found"
      clearable
      rightSection={
        loadingState || commonProps.rightSection ? <Loader size="xs" /> : null
      }
      disabled={loadingState}
      comboboxProps={{ zIndex: 1000 }}
    />
  );
};

export default SelectField;
